import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Menu as MenuIcon,
  X,
  ChevronDown,
  ChevronRight,
  Sparkles,
  Award,
  Heart,
  Image as ImageIcon,
  Info,
  Phone,
  Download,
  MapPin,
} from 'lucide-react';
import { FRANKIE_VIDEOS } from '../data/restaurantData';
import { usePWAInstall } from '../hooks/usePWAInstall';
import { useCart } from '../context/CartContext';
import { useImages } from '../context/ImageContext';
import { ClientImage } from './ClientImage';

// --- Line-Art Category Icons (Yellow) ---

const HomeIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 10.5L12 3l9 7.5" />
    <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
    <path d="M9 21v-6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v6" />
  </svg>
);

const FoodIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 11C4 7 7.5 4 12 4s8 3 8 7H4z" />
    <line x1="3" y1="14" x2="21" y2="14" />
    <path d="M5 17.5h14c0 2.2-2 3.5-7 3.5s-7-1.3-7-3.5z" />
  </svg>
);

const TreatsIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M7 11V6a5 5 0 0 1 10 0v5a3 3 0 0 1-3 3h-4a3 3 0 0 1-3-3z" />
    <line x1="12" y1="14" x2="12" y2="20" strokeWidth="2.5" />
    <line x1="10" y1="8" x2="10" y2="11" />
  </svg>
);

const BarIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 4h12l-1.5 15a2 2 0 0 1-2 1.8H9.5A2 2 0 0 1 7.5 19L6 4z" />
    <line x1="7" y1="8.5" x2="17" y2="8.5" />
  </svg>
);

const RidesIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M3 19c4.5 2 13.5 2 18 0" />
    <path d="M6.5 18l2.5-6h6l2.5 6" />
    <path d="M8.5 12h7" />
    <path d="M15.5 12l2-4.5h2l-1 2.5" />
    <path d="M7 11.5c-1.5-1-2.5 0-3 1" />
  </svg>
);

const HireIcon: React.FC = () => (
  <svg
    className="w-5 h-5 text-[#FFD000] group-hover:scale-110 transition-transform shrink-0"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3a7 7 0 0 1 7 7H5a7 7 0 0 1 7-7z" />
    <line x1="12" y1="10" x2="12" y2="19" />
    <path d="M4 19c2-1 4-1 6 0s4 1 6 0 3-.7 4 0" />
  </svg>
);

interface HeaderProps {}

export const Header: React.FC<HeaderProps> = () => {
  const { clientImages } = useImages();
  const { isInstallable, isInIframe, install } = usePWAInstall();
  const { setIsPwaModalOpen } = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(true);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Close "More" dropdown when clicked outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setMoreMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [mobileMenuOpen]);

  // "Get app" click action
  const handleGetApp = async () => {
    if (isInstallable && !isInIframe) {
      const outcome = await install();
      if (outcome) return;
    }
    setIsPwaModalOpen(true);
  };

  const isMoreActive =
    location.pathname.startsWith('/gallery') ||
    location.pathname.startsWith('/charity') ||
    location.pathname.startsWith('/tripadvisor') ||
    location.pathname.startsWith('/about') ||
    location.pathname.startsWith('/contact');

  return (
    <header className="sticky top-0 z-50 w-full bg-[#0050D8] bg-gradient-to-r from-[#0C4AC5] via-[#0E55DA] to-[#0A41B0] shadow-sm border-b border-blue-400/25">
      {/* ============================================================== */}
      {/* 1. DESKTOP VIEW: Visible ONLY on md screens and up (≥768px)    */}
      {/* ============================================================== */}
      <div className="hidden md:flex max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-1.5 lg:py-2 items-center justify-between w-full">
        
        {/* Real Frankie's Logo on Left - Sleek desktop proportion */}
        <Link
          to="/"
          className="flex items-center select-none group shrink-0 focus:outline-none"
          id="desktop-brand-logo"
          aria-label="Frankie's @ The Beach - Home"
        >
          <ClientImage
            slotKey="site:logo"
            src={clientImages.logo || '/logo.webp'}
            fallbackSrc="/logo.webp"
            alt="Frankie's @ The Beach"
            className="h-12 lg:h-14 xl:h-16 w-auto max-w-[170px] lg:max-w-[210px] xl:max-w-[240px] object-contain drop-shadow-md group-hover:scale-105 transition-transform"
            priority
          />
        </Link>

        {/* Desktop Nav Items: Center/Right-Center Group */}
        <div className="flex items-center gap-2.5 lg:gap-4 xl:gap-6 shrink-0">
          
          {/* Home */}
          <Link
            to="/"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-home"
            title="Home"
          >
            <HomeIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Home
            </span>
          </Link>

          {/* Food */}
          <Link
            to="/menu"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-food"
            title="Food Menu"
          >
            <FoodIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Food
            </span>
          </Link>

          {/* Desserts (Donuts, Waffles & Ice Creams) */}
          <Link
            to="/menu?category=donuts"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-desserts"
            title="Ice Creams & Desserts"
          >
            <TreatsIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Desserts
            </span>
          </Link>

          {/* Bar (Beach Bar Drinks) */}
          <Link
            to="/menu?category=alcohol"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-bar"
            title="Beach Bar & Drinks"
          >
            <BarIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Bar
            </span>
          </Link>

          {/* Rides (Kids' Rides) */}
          <Link
            to="/rides"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-rides"
            title="Kids' Rides"
          >
            <RidesIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Rides
            </span>
          </Link>

          {/* Hire (Beach Hire) */}
          <Link
            to="/beach-hire"
            className="flex flex-col items-center justify-center px-1.5 lg:px-2 py-0.5 rounded-lg hover:bg-white/10 active:scale-95 transition-all group cursor-pointer shrink-0"
            id="desktop-category-hire"
            title="Beach Hire"
          >
            <HireIcon />
            <span className="text-white text-[11px] lg:text-xs font-extrabold leading-tight mt-0.5 group-hover:text-[#FFD000] transition-colors whitespace-nowrap">
              Hire
            </span>
          </Link>

          {/* More ˅ Dropdown */}
          <div className="relative shrink-0" ref={moreMenuRef}>
            <button
              type="button"
              onClick={() => setMoreMenuOpen(!moreMenuOpen)}
              className={`flex items-center gap-1 text-xs lg:text-[13px] font-extrabold tracking-wide transition-colors cursor-pointer focus:outline-none whitespace-nowrap px-2 py-1 rounded-lg hover:bg-white/10 ${
                isMoreActive ? 'text-[#FFD000]' : 'text-white hover:text-[#FFD000]'
              }`}
              aria-expanded={moreMenuOpen}
              id="desktop-nav-more"
            >
              <span>More</span>
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform duration-200 ${
                  moreMenuOpen ? 'rotate-180 text-[#FFD000]' : 'text-white/80'
                }`}
              />
            </button>

            {/* More Dropdown Menu */}
            {moreMenuOpen && (
              <div
                className="absolute top-full right-0 mt-3 w-64 bg-white rounded-2xl shadow-2xl border border-gray-200 p-2 z-50 animate-in fade-in zoom-in-95 duration-150 ring-1 ring-black/5 text-left"
                role="menu"
                id="desktop-more-dropdown"
              >
                <div className="absolute -top-1.5 right-6 w-3 h-3 bg-white border-t border-l border-gray-200 rotate-45 z-10" />

                <div className="space-y-1">
                  {/* 1. Get App */}
                  <button
                    type="button"
                    onClick={() => {
                      setMoreMenuOpen(false);
                      handleGetApp();
                    }}
                    className="w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold text-gray-800 hover:bg-blue-50 hover:text-[#0055DA] transition-colors text-left cursor-pointer"
                    role="menuitem"
                    id="link-more-get-app"
                  >
                    <div className="flex items-center gap-2.5">
                      <Download className="w-4 h-4 text-[#0055DA]" />
                      <span>Get App</span>
                    </div>
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                      Install
                    </span>
                  </button>

                  {/* 2. About Us */}
                  <Link
                    to="/about"
                    onClick={() => setMoreMenuOpen(false)}
                    className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold text-gray-800 hover:bg-blue-50 hover:text-[#0055DA] transition-colors"
                    role="menuitem"
                    id="link-more-about"
                  >
                    <div className="flex items-center gap-2.5">
                      <Info className="w-4 h-4 text-indigo-600" />
                      <span>About Us</span>
                    </div>
                    <span className="text-[11px] text-gray-400 font-medium">Our Story</span>
                  </Link>

                  {/* 3. Gallery */}
                  <Link
                    to="/gallery"
                    onClick={() => setMoreMenuOpen(false)}
                    className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold text-gray-800 hover:bg-blue-50 hover:text-[#0055DA] transition-colors"
                    role="menuitem"
                    id="link-more-gallery"
                  >
                    <div className="flex items-center gap-2.5">
                      <ImageIcon className="w-4 h-4 text-sky-600" />
                      <span>Gallery</span>
                    </div>
                    <span className="text-[11px] text-gray-400 font-medium">Photos</span>
                  </Link>

                  {/* 4. Charity */}
                  <Link
                    to="/charity"
                    onClick={() => setMoreMenuOpen(false)}
                    className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold text-gray-800 hover:bg-blue-50 hover:text-[#0055DA] transition-colors"
                    role="menuitem"
                    id="link-more-charity"
                  >
                    <div className="flex items-center gap-2.5">
                      <Heart className="w-4 h-4 text-rose-500" />
                      <span>Charity</span>
                    </div>
                    <span className="text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                      Community
                    </span>
                  </Link>

                  {/* 5. TripAdvisor */}
                  <Link
                    to="/tripadvisor"
                    onClick={() => setMoreMenuOpen(false)}
                    className="flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm font-bold text-gray-800 hover:bg-blue-50 hover:text-[#0055DA] transition-colors"
                    role="menuitem"
                    id="link-more-tripadvisor"
                  >
                    <div className="flex items-center gap-2.5">
                      <Award className="w-4 h-4 text-emerald-600" />
                      <span>TripAdvisor</span>
                    </div>
                    <span className="text-[10px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                      UK #1
                    </span>
                  </Link>

                  {/* 6. Beach Videos */}
                  <div className="pt-1.5 mt-1 border-t border-gray-100">
                    <a
                      href="/#frankies-beach-videos-section"
                      onClick={(e) => {
                        setMoreMenuOpen(false);
                        const el = document.getElementById('frankies-beach-videos-section');
                        if (el) {
                          e.preventDefault();
                          el.scrollIntoView({ behavior: 'smooth' });
                          window.dispatchEvent(new CustomEvent('play-all-frankie-videos'));
                        }
                      }}
                      className="flex items-center justify-between px-3.5 py-2 rounded-xl text-xs font-bold text-gray-700 hover:bg-blue-50 hover:text-[#0055DA] transition-colors"
                    >
                      <span>Beach Videos 🎥</span>
                      <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-bold">
                        {FRANKIE_VIDEOS.length} clips
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* [FIND US] Yellow Button on Right */}
        <div className="flex items-center shrink-0">
          <Link
            to="/contact"
            className="bg-[#FFD000] hover:bg-[#FFD933] text-black font-heading font-black text-xs lg:text-xs uppercase tracking-wider px-4 lg:px-5 py-2 rounded-xl shadow-xs hover:shadow-md transition-all active:scale-95 cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            id="btn-find-us-desktop"
            title="Find Frankie's @ The Beach"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>FIND US</span>
          </Link>
        </div>
      </div>

      {/* ============================================================== */}
      {/* 2. MOBILE / TABLET VIEW: Visible ONLY below md (<768px)        */}
      {/* Exactly ONE logo on the left, FIND US and Menu on the right    */}
      {/* ============================================================== */}
      <div className="flex md:hidden max-w-7xl mx-auto px-3.5 sm:px-5 py-2 items-center justify-between w-full gap-2">
        
        {/* Left: Real Frankie's Logo - Much Bigger on Mobile/Tablet */}
        <Link
          to="/"
          className="flex items-center select-none active:scale-95 transition-all focus:outline-none"
          id="mobile-brand-link"
          aria-label="Frankie's @ The Beach - Home"
        >
          <ClientImage
            slotKey="site:logo"
            src={clientImages.logo || '/logo.webp'}
            fallbackSrc="/logo.webp"
            alt="Frankie's @ The Beach"
            className="h-14 sm:h-16 w-auto max-w-[175px] sm:max-w-[210px] object-contain drop-shadow-md"
            priority
          />
        </Link>

        {/* Right: FIND US button + Hamburger Menu Button */}
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to="/contact"
            className="bg-[#FFD000] hover:bg-[#FFD933] text-black font-heading font-black text-xs uppercase tracking-wider px-3.5 py-2 rounded-lg shadow-xs active:scale-95 transition-all cursor-pointer whitespace-nowrap flex items-center gap-1.5"
            id="btn-find-us-mobile"
            title="Find Frankie's @ The Beach"
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>FIND US</span>
          </Link>

          {/* Hamburger Button (☰) */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-9 h-9 flex items-center justify-center text-white hover:text-[#FFD000] rounded-lg bg-white/10 hover:bg-white/20 active:bg-white/30 transition-colors cursor-pointer border border-white/20"
            aria-label="Toggle navigation menu"
            id="btn-mobile-toggle"
          >
            {mobileMenuOpen ? <X className="w-4.5 h-4.5" /> : <MenuIcon className="w-4.5 h-4.5" />}
          </button>
        </div>
      </div>

      {/* ============================================================== */}
      {/* MOBILE MENU DRAWER (Revealed on ☰ click, sliding down from top) */}
      {/* ============================================================== */}
      {mobileMenuOpen && (
        <div
          className="md:hidden w-full bg-[#0D2460] border-t border-white/15 px-3.5 sm:px-5 py-4 space-y-4 rounded-b-3xl animate-in slide-in-from-top duration-200 shadow-2xl overflow-y-auto overscroll-contain pb-6 text-left relative z-20"
          style={{
            maxHeight: 'calc(100dvh - 5rem)',
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
          }}
          id="mobile-navigation-drawer"
        >
          {/* 6 Category Quick Action Cards (Home, Food, Treats, Bar, Rides, Hire) */}
          <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
            {/* 1. Home */}
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-home"
            >
              <HomeIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Home</span>
            </Link>

            {/* 2. Food */}
            <Link
              to="/menu"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-food"
            >
              <FoodIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Food</span>
            </Link>

            {/* 3. Desserts */}
            <Link
              to="/menu?category=donuts"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-desserts"
            >
              <TreatsIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Desserts</span>
            </Link>

            {/* 4. Bar */}
            <Link
              to="/menu?category=alcohol"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-bar"
            >
              <BarIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Bar</span>
            </Link>

            {/* 5. Rides */}
            <Link
              to="/rides"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-rides"
            >
              <RidesIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Rides</span>
            </Link>

            {/* 6. Hire */}
            <Link
              to="/beach-hire"
              onClick={() => setMobileMenuOpen(false)}
              className="flex flex-col items-center justify-center py-2.5 sm:py-3 px-0.5 sm:px-1 rounded-2xl bg-[#1A4EB8] hover:bg-[#2563EB] active:scale-95 transition-all text-center group"
              id="mobile-card-hire"
            >
              <HireIcon />
              <span className="text-white text-[11px] sm:text-xs font-bold mt-1 sm:mt-1.5 leading-tight truncate">Hire</span>
            </Link>
          </div>

          {/* Navigation List Rows */}
          <div className="space-y-0 divide-y divide-white/10 border-t border-b border-white/10">
            {/* Full menu */}
            <Link
              to="/menu"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between py-3.5 px-1 text-white hover:text-[#FFD000] text-[15px] sm:text-base font-semibold transition-colors group"
              id="mobile-link-full-menu"
            >
              <span>Full menu</span>
              <ChevronRight className="w-5 h-5 text-white/50 group-hover:text-[#FFD000] group-hover:translate-x-0.5 transition-all" />
            </Link>

            {/* Reviews -> UK #1 on TripAdvisor */}
            <Link
              to="/reviews"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between py-3.5 px-1 text-white hover:text-[#FFD000] text-[15px] sm:text-base font-semibold transition-colors group"
              id="mobile-link-reviews"
            >
              <span>Reviews</span>
              <div className="flex items-center gap-1.5">
                <span className="text-xs sm:text-sm font-normal text-white/70">
                  UK #1 on TripAdvisor
                </span>
                <ChevronRight className="w-5 h-5 text-white/50 group-hover:text-[#FFD000] group-hover:translate-x-0.5 transition-all" />
              </div>
            </Link>

            {/* Photos and videos */}
            <Link
              to="/gallery"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between py-3.5 px-1 text-white hover:text-[#FFD000] text-[15px] sm:text-base font-semibold transition-colors group"
              id="mobile-link-photos"
            >
              <span>Photos and videos</span>
              <ChevronRight className="w-5 h-5 text-white/50 group-hover:text-[#FFD000] group-hover:translate-x-0.5 transition-all" />
            </Link>

            {/* About Us (just "About Us" as requested) */}
            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center justify-between py-3.5 px-1 text-white hover:text-[#FFD000] text-[15px] sm:text-base font-semibold transition-colors group"
              id="mobile-link-about"
            >
              <span>About Us</span>
              <ChevronRight className="w-5 h-5 text-white/50 group-hover:text-[#FFD000] group-hover:translate-x-0.5 transition-all" />
            </Link>
          </div>

          {/* Bottom Action Buttons: Find us & Get app */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            {/* Find us */}
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="bg-[#FFD000] hover:bg-[#FFE033] text-black rounded-2xl py-3 px-2 flex flex-col items-center justify-center shadow-md active:scale-95 transition-all text-center"
              id="mobile-btn-find-us"
            >
              <div className="flex items-center gap-1.5 text-sm sm:text-base font-black">
                <MapPin className="w-4 h-4 fill-black/20" />
                <span>Find us</span>
              </div>
              <span className="text-[11px] sm:text-xs font-bold text-black/80 mt-0.5">
                CT11 8LS
              </span>
            </Link>

            {/* Get app */}
            <button
              type="button"
              onClick={() => {
                setMobileMenuOpen(false);
                handleGetApp();
              }}
              className="border-2 border-[#FFD000] bg-transparent hover:bg-white/5 active:bg-white/10 text-white rounded-2xl py-3 px-2 flex flex-col items-center justify-center active:scale-95 transition-all text-center cursor-pointer"
              id="mobile-btn-get-app"
            >
              <div className="flex items-center gap-1.5 text-sm sm:text-base font-black text-white">
                <Download className="w-4 h-4 text-[#FFD000]" />
                <span>Get app</span>
              </div>
              <span className="text-[11px] sm:text-xs font-medium text-white/70 mt-0.5">
                Free install
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-xs -z-10 md:hidden animate-in fade-in duration-200"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  );
};
